import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  ckConfirm,
  Dialog,
  EmptyState,
  Field,
  Panel,
  Spinner,
  Switch,
  Tag,
  TextArea,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { mailingApi, type SignupFormDto, type SignupFormInput } from "../../api/mailingApi";
import { useShell } from "../shell/ShellContext";
import { copyText, errorMessage, fmtNum } from "./mailingUi";

type Draft = SignupFormInput & { id: string | null };

function FormDialog({ draft, onClose, onSaved }: { draft: Draft | null; onClose: () => void; onSaved: (f: SignupFormDto) => void }) {
  const toast = useCkToast();
  const [d, setD] = useState<Draft | null>(draft);
  const [busy, setBusy] = useState(false);
  useEffect(() => setD(draft), [draft]);
  if (!d) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { id, ...input } = d;
      const f = await mailingApi.forms.save(id, input);
      toast.success("הטופס נשמר");
      onSaved(f);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size="md"
      icon="note"
      title={d.id ? "עריכת טופס הרשמה" : "טופס הרשמה חדש"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={busy || !d.title?.trim()}>
            {busy ? "שומר…" : "שמירה"}
          </Button>
        </>
      }
    >
      <div className="ml-stack">
        <Field label="כותרת" full>
          <TextInput value={d.title ?? ""} onChange={(e) => setD({ ...d, title: e.target.value })} maxLength={200} autoFocus placeholder="הצטרפו לעדכונים שלנו" />
        </Field>
        <Field label="תיאור (רשות)" full>
          <TextArea rows={3} value={d.description ?? ""} onChange={(e) => setD({ ...d, description: e.target.value })} maxLength={1000} />
        </Field>
        <div className="ml-grid2">
          <Field label="טקסט הכפתור">
            <TextInput value={d.buttonLabel ?? ""} onChange={(e) => setD({ ...d, buttonLabel: e.target.value })} maxLength={60} placeholder="הרשמה" />
          </Field>
          <Field label="הודעה אחרי הרשמה">
            <TextInput value={d.successMessage ?? ""} onChange={(e) => setD({ ...d, successMessage: e.target.value })} maxLength={300} placeholder="תודה! נרשמת בהצלחה." />
          </Field>
        </div>
        <Switch checked={d.collectName !== false} onChange={(v) => setD({ ...d, collectName: v })} label="לבקש גם שם" />
        <Switch
          checked={d.doubleOptIn !== false}
          onChange={(v) => setD({ ...d, doubleOptIn: v })}
          label="אישור כפול (מומלץ)"
          hint="הנרשם מקבל מייל עם כפתור אישור, ורק אחרי האישור נכנס לרשימה. מונע הרשמות מזויפות ושומר על מוניטין השליחה."
        />
        <Switch checked={Boolean(d.welcomeEnabled)} onChange={(v) => setD({ ...d, welcomeEnabled: v })} label="לשלוח מייל ברוכים הבאים אחרי ההרשמה" />
        <Switch checked={d.isActive !== false} onChange={(v) => setD({ ...d, isActive: v })} label="הטופס פעיל" />
      </div>
    </Dialog>
  );
}

/** Public signup forms — a link to share and an iframe to embed on the organization's site. */
export function MailingForms() {
  const toast = useCkToast();
  const navigate = useNavigate();
  const { allowed } = useShell();
  const canManage = allowed("mailing.manage");
  const [items, setItems] = useState<SignupFormDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = useCallback(() => {
    mailingApi.forms
      .list()
      .then(setItems)
      .catch((e) => setError(errorMessage(e, "לא הצלחנו לטעון את הטפסים")));
  }, []);
  useEffect(load, [load]);

  const copy = async (text: string, what: string) => {
    if (await copyText(text)) toast.success(`${what} הועתק`);
    else toast.error("ההעתקה נכשלה — סמנו והעתיקו ידנית");
  };

  const toggle = async (f: SignupFormDto, patch: SignupFormInput) => {
    try {
      await mailingApi.forms.save(f.id, { title: f.title, description: f.description, buttonLabel: f.buttonLabel, successMessage: f.successMessage, collectName: f.collectName, doubleOptIn: f.doubleOptIn, isActive: f.isActive, ...patch });
      load();
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    }
  };

  const remove = async (f: SignupFormDto) => {
    if (!(await ckConfirm({ title: "למחוק את הטופס?", message: `הקישור לטופס "${f.title}" יפסיק לעבוד. המנויים שנרשמו בו נשארים.`, confirmLabel: "מחיקה", danger: true }))) return;
    try {
      await mailingApi.forms.remove(f.id);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "המחיקה נכשלה"));
    }
  };

  return (
    <div className="ml-stack">
      {canManage ? (
        <div className="ml-row ml-row--end">
          <Button icon="plus" onClick={() => setDraft({ id: null, title: "", doubleOptIn: true, collectName: true, isActive: true, welcomeEnabled: false })}>
            טופס חדש
          </Button>
        </div>
      ) : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {!items ? (
        <div className="ml-busy">
          <Spinner size={24} />
        </div>
      ) : !items.length ? (
        <EmptyState icon="note" title="עדיין אין טפסי הרשמה">
          טופס הרשמה אוסף מנויים לדיוור באתר שלכם או בקישור — עם אישור כפול ומייל ברוכים הבאים.
        </EmptyState>
      ) : (
        items.map((f) => (
          <Panel
            key={f.id}
            title={f.title}
            icon="note"
            sub={`${fmtNum(f.subscribers)} מנויים${f.pending ? ` · ${fmtNum(f.pending)} ממתינים לאישור` : ""}`}
            actions={
              <div className="ml-row">
                {f.isActive ? <Tag tone="success">פעיל</Tag> : <Tag tone="neutral">מושבת</Tag>}
                {canManage ? (
                  <>
                    <Button size="sm" icon="edit" onClick={() => setDraft({ ...f, id: f.id, welcomeEnabled: Boolean(f.welcome?.enabled) })}>
                      עריכה
                    </Button>
                    <Button size="sm" variant="ghost" icon="trash" iconOnly aria-label="מחיקה" onClick={() => void remove(f)} />
                  </>
                ) : null}
              </div>
            }
          >
            <div className="ml-stack">
              <div className="ml-copy">
                <span className="ml-copy__label">קישור</span>
                <code dir="ltr">{f.url}</code>
                <Button size="sm" icon="copy" onClick={() => void copy(f.url, "הקישור")}>
                  העתקה
                </Button>
                <Button size="sm" variant="ghost" icon="external" onClick={() => window.open(f.url, "_blank", "noopener")}>
                  פתיחה
                </Button>
              </div>
              <div className="ml-copy">
                <span className="ml-copy__label">הטמעה באתר</span>
                <code dir="ltr">{f.embed}</code>
                <Button size="sm" icon="copy" onClick={() => void copy(f.embed, "קוד ההטמעה")}>
                  העתקה
                </Button>
              </div>
              <div className="ml-row ml-row--between">
                <div className="ml-row">
                  <span className="ml-muted">{f.doubleOptIn ? "אישור כפול" : "הרשמה מיידית"}</span>
                  <span className="ml-muted">·</span>
                  {f.welcome?.enabled ? (
                    <span className="ml-muted">מייל ברוכים הבאים: נשלח ל־{fmtNum(f.welcome.sent)}</span>
                  ) : (
                    <span className="ml-muted">בלי מייל ברוכים הבאים</span>
                  )}
                </div>
                {canManage ? (
                  <div className="ml-row">
                    {f.welcome ? (
                      <Button size="sm" icon="edit" onClick={() => navigate(`/mailing/${f.welcome!.blastId}`)}>
                        עריכת מייל הברוכים הבאים
                      </Button>
                    ) : null}
                    <Switch checked={f.isActive} onChange={(v) => void toggle(f, { isActive: v })} label="פעיל" />
                  </div>
                ) : null}
              </div>
            </div>
          </Panel>
        ))
      )}
      <FormDialog draft={draft} onClose={() => setDraft(null)} onSaved={() => load()} />
    </div>
  );
}
